import { Flex, Modal, Spin } from 'antd'
import { isArray, isEmpty, isNil, set } from 'lodash-es'
import { FunctionComponent, ReactNode, useState } from 'react'
import styled from 'styled-components'
import { useDeleteDirectoryKolBasicInfoMutation } from '@/api/workspace-api/directory-api'
import { StyledBrandTag } from '@/components/common/tags'
import Actions from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/actions'
import BasicInfoModal from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/basic-info-modal'
import Header from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/header'
import { useKolContactInfo } from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/use-kol-contact-info'
import { ActionsWrapper } from '@/components/kol/irm/common/action-wrapper'
import useNotification from '@/hooks/antd/use-notification'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import { BasicInfo } from '@/types/schema/directory/kol-management'
import logger from '@/utils/logger'

const baseInfoItemI18nMap: Record<keyof BasicInfo, I18nId> = {
  birthday: 'kol:field_birthday',
  profession: 'directory:search_field_type_profession',
  address: 'general:field_location',
  customized_labels: 'directory:search_field_type_tags',
}

interface BasicInfoFormProps {
  kolId: string
  responseData?: BasicInfo
  isFetching: boolean
}

const BasicInfoForm: FunctionComponent<BasicInfoFormProps> = ({
  kolId,
  responseData,
  isFetching,
}) => {
  const { formatMessage, formatDate } = useIntl()
  const [openModal, setOpenModal] = useState(false)
  const [modal, contextHolder] = Modal.useModal()
  const notification = useNotification()

  const [deleteBasicInfo] = useDeleteDirectoryKolBasicInfoMutation()
  const { handleEditBasicInfoEvent } = useKolContactInfo(kolId)

  const isCreate =
    isNil(responseData) || Object.values(responseData).every(isEmpty)

  const handleOpenModal = (): void => {
    setOpenModal(true)
  }

  const handleDelete = async (key: keyof BasicInfo): Promise<void> => {
    if (!responseData) {
      return
    }

    modal.confirm({
      centered: true,
      title: formatMessage({ id: 'general:confirm_delete_title' }),
      content: formatMessage({
        id: 'general:confirm_delete_description',
      }),
      cancelText: formatMessage({ id: 'general:btn_text_cancel' }),
      okText: formatMessage({ id: 'general:btn_text_confirm' }),
      onOk: async (): Promise<void> => {
        try {
          const basicInfo = set(
            { ...responseData },
            key,
            key === 'customized_labels' ? [] : '',
          )
          await deleteBasicInfo({
            kolId,
            deleteField: key === 'customized_labels' ? 'customized_label' : key,
          }).unwrap()
          handleEditBasicInfoEvent(basicInfo)
        } catch (error) {
          logger.error(error)
          notification.error({
            message: formatMessage({ id: 'general:operation_failure' }),
            placement: 'bottomRight',
          })
        }
      },
    })
  }

  return (
    responseData && (
      <Wrapper spinning={isFetching}>
        <Header
          titleI18nId='kol:edit_title_basic_info'
          onClickAddButton={isCreate ? handleOpenModal : undefined}
        />
        <Flex vertical>
          {Object.entries(baseInfoItemI18nMap).map(
            ([key, i18nId]: [keyof BasicInfo, I18nId]) => {
              const value = responseData[key]

              return (
                <InfoItem key={key}>
                  <div>{formatMessage({ id: i18nId })}</div>
                  <Flex flex={1} justify='space-between'>
                    <div>
                      {((): ReactNode => {
                        if (
                          key === 'customized_labels' &&
                          Array.isArray(value)
                        ) {
                          return (
                            <Flex gap={4} wrap='wrap'>
                              {value.map((label) => (
                                <StyledBrandTag key={label.id}>
                                  {label.name}
                                </StyledBrandTag>
                              ))}
                            </Flex>
                          )
                        }

                        if (isArray(value)) {
                          return null
                        }

                        if (key === 'birthday' && value) {
                          return formatDate(value)
                        }

                        return value
                      })()}
                    </div>
                    <Actions
                      onDelete={
                        isEmpty(value)
                          ? undefined
                          : (): Promise<void> => handleDelete(key)
                      }
                      onEdit={handleOpenModal}
                    />
                  </Flex>
                </InfoItem>
              )
            },
          )}
        </Flex>
        <BasicInfoModal
          data={responseData}
          isCreate={isCreate}
          kolId={kolId}
          open={openModal}
          onClose={(): void => setOpenModal(false)}
        />
        {contextHolder}
      </Wrapper>
    )
  )
}

export default BasicInfoForm

const InfoItem = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  padding: 12px 0;

  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 14px;
  line-height: 140%;
  border-bottom: 1px solid ${({ theme }): string => theme.colors.border.divider};
  min-height: 32px;

  > div:first-child {
    min-width: 80px;
  }

  :hover ${ActionsWrapper} {
    visibility: visible;
  }
`

const Wrapper = styled(Spin)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
